import { useEffect, useMemo, useState } from 'react';
import { Form, Input, InputNumber, message, Card, Button, Typography, Table, Spin, Descriptions, Tag, List } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { RuleObject } from 'antd/es/form';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useRouter } from '@tanstack/react-router';
import { z } from 'zod';

import type { ClaimRequestType, ServicesType } from '../../types/claimType';
import { post } from '@/common/services/httpClient';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import { formatPaddedId } from '@/common/helpers/formatHelpers';
import AmountFormat from '@/common/components/AmountFormat';
import { buildClaimStatusAuditHistory } from "@/common/utils/auditUtils";
import { useAppQuery } from '@/common/hooks/useAppQuery';
import { ClaimStatus } from '@/common/staticData/enums';
import { currencyAmountValidator } from '../../../../common/components/MultipleOfValidator';

const { Title } = Typography;

interface ApiResponse<T = unknown> {
    isSuccess?: boolean;
    isError?: boolean;
    message?: string;
    data?: T;
}

interface UpdateClaimStatusPayload {
    id: number;
    approvedAmount: number;
    reason: string;
    status: "Approved" | "Rejected";
    auditHistory: unknown;
    services: ServicesType[];
}

const approveRejectStateSchema = z.object({
    claimId: z.number(),
    PolicyHolderId: z.number(),
    type: z.enum(['approve', 'reject']),
});

const ApproveRejectClaimForm = ({ claimId, policyHolderId, modalType }: { claimId: number; policyHolderId: number; modalType: 'approve' | 'reject' }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const router = useRouter();
    const queryClient = useQueryClient();
    const [services, setServices] = useState<ServicesType[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const watchedServices = Form.useWatch('services', form);

    const { data: claim, isLoading } = useAppQuery(
        [QUERY_KEYS.CLAIM.GET_BY_ID, claimId, policyHolderId],
        async (): Promise<ClaimRequestType> => {
            const response = await post<ApiResponse<ClaimRequestType>>(API_ENDPOINTS.CLAIM.GET_CLAIM_SERVICES, { claimId, policyHolderId });
            if (!response.data) {
                throw new Error("Claim not found");
            }
            return response.data;
        },
        { enabled: !!claimId && !!policyHolderId }
    );

    const watchedApprovedAmount = Form.useWatch('approvedAmount', form);


    useEffect(() => {
        if (claim) {
            form.resetFields();
            if (claim.services) {
                const setPayableAmount = claim.services.map(s => ({
                    ...s,
                    payableAmount: s.payableAmount < 0 ? 0 : s.payableAmount,
                }));
                setServices(setPayableAmount);

                const serviceValues = setPayableAmount.reduce((acc: Record<string, { payableAmount: number | null }>, s) => {
                    const key = `${s.serviceCategoryItemId}_${s.serviceName}`;
                    acc[key] = { payableAmount: s.payableAmount };
                    //acc[s.serviceCategoryItemId] = { payableAmount: s.payableAmount };
                    return acc;
                }, {} as Record<number, { payableAmount: number | null }>);
                form.setFieldsValue({ services: serviceValues });
            }

            if (modalType === "approve" && claim.status === ClaimStatus.Approved) {
                form.setFieldsValue({
                    approvedAmount: claim.approvedAmount,
                    reason: claim.reason,
                });
            } else if (modalType === "reject" && claim.status === ClaimStatus.Rejected) {
                form.setFieldsValue({
                    reason: claim.reason,
                });
            }
        }
    }, [claim, modalType, form]);

    const validateApprovedAmount = (_rule: RuleObject, value: number) => {
        const balance = claim?.balance ?? 0;
        if (value === undefined || value === null) return Promise.reject(t("Approved amount is required"));
        if (value <= 0) return Promise.reject(t("Approved amount must be greater than zero"));
        if (value > balance) return Promise.reject(t(`Approved amount cannot exceed current balance`) + balance);
        if (value > finalMaxApprovedAmount) return Promise.reject(t(`Approved amount cannot exceed maximum payable amount`) + finalMaxApprovedAmount);
        return Promise.resolve();
    };

    const handleSave = async () => {
        setIsSubmitting(true);
        try {
            if (!claim || !modalType) {
                setIsSubmitting(false);
                return;
            }

            await form.validateFields();
            const values = form.getFieldsValue();

            const updatedServices = services.map(s => {
                const key = `${s.serviceCategoryItemId}_${s.serviceName}`;

                return {
                    ...s,
                    payableAmount:
                        values.services?.[key]?.payableAmount ?? s.payableAmount,
                };
            });

            //const updatedServices = services.map(s => ({
            //    ...s,
            //    payableAmount: values.services?.[s.serviceCategoryItemId]?.payableAmount ?? s.payableAmount,
            //}));

            let endpoint = API_ENDPOINTS.CLAIM.UPDATE_STATUS;
            const newStatus = modalType === "approve" ? "Approved" : "Rejected";

            const payload: UpdateClaimStatusPayload = {
                id: claim.id,
                approvedAmount: modalType === "approve" ? values.approvedAmount : 0,
                reason: values.reason ?? "",
                status: newStatus,
                auditHistory: buildClaimStatusAuditHistory(claim, values, modalType as "approve" | "reject"),
                services: updatedServices,
            };

            if (modalType === "reject" && !payload.reason) {
                message.error(t("reason is required to reject the claim."));
                setIsSubmitting(false);
                return;
            }

            if (modalType === "approve" && payload.approvedAmount < (claim.totalAmount ?? 0) && !payload.reason) {
                message.error(t("remarks is required when approved amount is less than total amount."));
                setIsSubmitting(false);
                return;
            }

            if (modalType === "approve") {
                endpoint = API_ENDPOINTS.CLAIM.APPROVED_STATUS ?? API_ENDPOINTS.CLAIM.UPDATE_STATUS;
            } else {
                endpoint = API_ENDPOINTS.CLAIM.REJECTED_STATUS ?? API_ENDPOINTS.CLAIM.UPDATE_STATUS;
            }

            try {
                const response = await post<ApiResponse>(endpoint, payload);
                if (!response?.isError) {
                    message.success(
                        modalType === "approve"
                            ? t("Claim approved successfully")
                            : t("Claim rejected successfully")
                    );
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLAIM.SEARCH] });
                    router.navigate({ to: '/claim/requests' });
                } else {
                    message.error(t(response?.message || "Failed to update claim status"));
                }
            } catch (err) {
                message.error(t("An error occurred while updating the status"));
            } finally {
                setIsSubmitting(false);
            }
        } catch (validationErr) {
            setIsSubmitting(false);
        }
    };

    // ------------------- Category-Based Services -------------------
    const servicesByCategory = useMemo(() => {
        if (!services?.length) return [];
        const map = new Map<number, {
            serviceCategoryId: number;
            serviceCategoryName: string;
            coveredAmount: number;
            balance?: number;
            services: ServicesType[];
        }>();

        services.forEach(s => {
            if (!map.has(s.serviceCategoryId)) {
                map.set(s.serviceCategoryId, {
                    serviceCategoryId: s.serviceCategoryId,
                    serviceCategoryName: s.serviceCategoryName,
                    coveredAmount: s.coveredAmount,
                    balance: s.balance,
                    services: [],
                });
            }
            map.get(s.serviceCategoryId)!.services.push(s);
        });

        return Array.from(map.values());
    }, [services]);

    const categoryColumns = useMemo(() => [
        { title: t('Main Service'), dataIndex: 'serviceCategoryName', key: 'serviceCategoryName' },
        {
            title: t('Covered Amount'),
            dataIndex: 'coveredAmount',
            key: 'coveredAmount',
            render: (v: number) => <AmountFormat value={v} currencyCode={claim?.currencyISOCode} />,
        },
        {
            title: t('Balance'),
            dataIndex: 'balance',
            key: 'balance',
            render: (v: number) => <AmountFormat value={v} currencyCode={claim?.currencyISOCode} />,
        },
    ], [t, claim]);

    const renderServiceItems = (category: any) => {
        const categoryBalance = category.balance ?? 0;
        const isZeroBalance = categoryBalance === 0;

        return (
            <Table
                dataSource={category.services}
                rowKey="serviceName"
                pagination={false}
                size="small"

                expandable={{
                    // ✅ Expand all rows that have child exceptions
                    defaultExpandedRowKeys: category.services
                        ?.filter((x) => x.customerExceptions?.length)
                        .map((x) => x.serviceName),

                    expandedRowRender: (record: ServicesType) => {
                        if (!record.customerExceptions?.length) {
                            return null;
                        }

                        return (
                            <div style={{ padding: "8px 24px", background: "#fafafa" }}>
                                <Typography.Text strong>
                                    {t("Exceptions")}
                                </Typography.Text>

                                <div style={{ marginTop: 8 }}>
                                    {record.customerExceptions.map((ex) => (
                                        <Tag
                                            key={ex.name}
                                            color="orange"
                                            style={{ marginBottom: 4 }}
                                        >
                                            {ex.name}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        );
                    },
                    // showExpandColumn: false, // hide alwasy
                    rowExpandable: (record) =>
                        !!record.customerExceptions?.length,
                }}

                onRow={(record: ServicesType) => {
                    const isNoAmountCoverage = record.isAmountCoverage === false;

                    if (isNoAmountCoverage) {
                        return {
                            style: {
                                backgroundColor: '#fff1f0', // light red
                            },
                        };
                    }

                    if (isZeroBalance) {
                        return {
                            style: {
                                backgroundColor: '#fff1f0', // light yellow
                            },
                        };
                    }

                    return {};
                }}
                components={{
                    header: {
                        cell: (props: any) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: '#f0f7ff',   // 🔥 child header color
                                    color: '#1d39c4',
                                    fontWeight: 600,
                                    borderBottom: '1px solid #adc6ff',
                                    padding: '12px 8px',
                                }}
                            />
                        ),
                    },
                }}
                columns={[
                    { title: t('Sub Service'), dataIndex: 'serviceName', key: 'serviceName' },
                    {
                        title: t('Claim Amount'),
                        dataIndex: 'amount',
                        key: 'amount',
                        render: (v: number) => <AmountFormat value={v} currencyCode={claim?.currencyISOCode} />,
                    },
                    {
                        title: t('Co-Share'),
                        dataIndex: 'coShare',
                        key: 'coShare',
                        render: (v: number) => v != null ? `${v}%` : '-',
                    },
                    // removed co-share amount
                    //{
                    //    title: t('Co-Share Amount'),
                    //    dataIndex: 'coShareAmount',
                    //    key: 'coShareAmount',
                    //    render: (v: number) => v != null ? <AmountFormat value={v} currencyCode={claim?.currencyISOCode} /> : '-',
                    //},
                    {
                        title: t('Payable Amount'),
                        key: 'payableAmount',
                        width: 200,
                        render: (_: any, record: ServicesType) => {
                            const maxByCoShare = (record.amount || 0) - (record.coShareAmount || 0);
                            const maxByBalance = record.balance ?? 0;
                            const finalMax = record?.isAmountCoverage ? Math.min(maxByCoShare, maxByBalance) : maxByCoShare;

                            return (
                                <Form.Item
                                    name={['services', `${record.serviceCategoryItemId}_${record.serviceName}`, 'payableAmount']}
                                    rules={[
                                        currencyAmountValidator(claim?.currencyISOCode, ""),
                                        {
                                            validator: async () => {
                                                if (categoryBalance === 0) return Promise.resolve();

                                                const values = form.getFieldsValue();
                                                const totalInCategory = category.services.reduce(
                                                    (sum: number, s: ServicesType) => {
                                                        const key = `${s.serviceCategoryItemId}_${s.serviceName}`;

                                                        return (
                                                            sum +
                                                            (values.services?.[key]?.payableAmount ??
                                                                s.payableAmount ??
                                                                0)
                                                        );
                                                    },
                                                    0
                                                );

                                                if (totalInCategory > categoryBalance) {
                                                    return Promise.reject(t(`Total payable amount in this category cannot exceed main service balance (${categoryBalance})`));
                                                }
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                    initialValue={record.payableAmount}
                                >
                                    <InputNumber min={0} max={finalMax} style={{ width: '100%' }}
                                        parser={(v) => (v ? v : '0')}
                                        disabled={categoryBalance === 0}
                                        />
                                </Form.Item>
                            )
                        }
                    },
                ]}
            />
        );
    };

    const totalPayable = useMemo(() => {
        if (!services || services.length === 0) return 0;

        return services.reduce((sum, s) => {
            const key = `${s.serviceCategoryItemId}_${s.serviceName}`;

            return (
                sum +
                (watchedServices?.[key]?.payableAmount ??
                    s.payableAmount ??
                    0)
            );
        }, 0);
    }, [services, watchedServices]);


    const finalMaxApprovedAmount = useMemo(() => {
        return totalPayable;
    }, [totalPayable]);

    useEffect(() => {
        if (
            modalType === "approve" &&
            claim &&
            (claim.approvedAmount === null || claim.approvedAmount === undefined || claim.approvedAmount === 0)
        ) {
            form.setFieldsValue({ approvedAmount: finalMaxApprovedAmount });
        }
    }, [finalMaxApprovedAmount, modalType, claim, form]);


    useEffect(() => {
        if (modalType !== "approve" || !claim) return;

        const calculatedApprovedAmount = totalPayable;

        form.setFieldsValue({
            approvedAmount: calculatedApprovedAmount,
        });
    }, [
        totalPayable,
        modalType,
    ]);



    const combineRemarks = (services: any[], currencyISOCode?: string) => {
        const remarks: string[] = [];

        services?.forEach((s) => {
            // 1️⃣ Backend remarks (category-level)
            if (s.remarks && s.remarks.trim()) {
                remarks.push(s.remarks.trim());
            }

            // 2️⃣ Co-Share remarks (service-level)
            if (typeof s.coShareAmount === "number" && s.coShareAmount > 0) {
                remarks.push(
                    `Co-Share amount deducted for service (${s.serviceName}) of ${s.coShareAmount} ${currencyISOCode ?? ''}`.trim()
                );
            }
        });

        // 3️⃣ Remove duplicates & format
        return Array.from(new Set(remarks))
            .map(r => `• ${r}`)
            .join('\n');
    };


    useEffect(() => {
        if (!servicesByCategory?.length) return;

        const allServices = servicesByCategory.flatMap(
            (cat: any) => cat.services || []
        );

        const combinedRemarks = combineRemarks(
            allServices,
            claim?.currencyISOCode
        );

        if (combinedRemarks) {
            form.setFieldsValue({
                reason: combinedRemarks,
            });
        }
    }, [servicesByCategory, claim?.currencyISOCode]);


    // ------------------- Render -------------------
    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Card>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 24,
                    borderBottom: "1px solid #f0f0f0",
                    paddingBottom: 16,
                }}
            >
                <Title level={3} style={{ margin: 0 }}>
                    {modalType === "approve" ? t("Approve Claim") : t("Reject Claim")}
                </Title>

                <Button type="default" onClick={() => router.navigate({ to: "/claim/requests" })}>
                    {t("common.goBack")}
                </Button>
            </div>

            <Form form={form} layout="vertical" onFinish={handleSave} style={{ padding: '0 24px' }}>

                {claim && (
                    <div style={{ marginBottom: 32 }}>
                        <Title level={4} style={{ marginBottom: 16 }}>
                            {t("Details")}
                        </Title>
                        <Descriptions
                            bordered
                            size="middle"
                            column={{ xs: 1, sm: 2, md: 3 }}
                            labelStyle={{
                                fontWeight: 600,
                                width: 180,
                                whiteSpace: "nowrap",
                            }}
                            contentStyle={{
                                wordBreak: "break-word",
                            }}
                            style={{ marginBottom: 24 }}
                        >
                            <Descriptions.Item label={t("Claim No")}>
                                {claim?.id ? formatPaddedId(Number(claim.id)) : "N/A"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Reference No")}>
                                {claim?.referenceNo || "N/A"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Card Number")}>
                                {claim?.cardNumber || "N/A"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Policy Holder")}>
                                {claim?.policyHolderName || "N/A"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Patient Name")}>
                                {claim?.patientName || "N/A"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Claim Date")}>
                                {claim?.claimDate
                                    ? dayjs(claim.claimDate).format("DD-MM-YYYY")
                                    : "N/A"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Claimed Amount")}>
                                <AmountFormat
                                    value={claim?.totalAmount ?? 0}
                                    currencyCode={claim?.currencyISOCode}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Balance")}>
                                <AmountFormat
                                    value={(claim?.balance ?? 0)}
                                    currencyCode={claim?.currencyISOCode}
                                />
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                )}

                {/* Customer General Exceptions Section */}

                {claim?.customerGeneralExceptions && claim.customerGeneralExceptions.length > 0 && (
                    <Card
                        title={t("General Exceptions")}
                        bordered={false}
                        style={{
                            marginBottom: 32,
                            backgroundColor: '#fffbe6', // soft yellow
                            border: '1px solid #ffe58f',
                        }}
                        headStyle={{ fontWeight: 600 }}
                    >
                        <List
                            size="small"
                            dataSource={claim.customerGeneralExceptions}
                            renderItem={(item, index) => (
                                <List.Item style={{ paddingLeft: 0, paddingRight: 0 }}>
                                    <strong>{index + 1}.</strong> {item.name}
                                </List.Item>
                            )}
                        />
                    </Card>
                )}


                {servicesByCategory.length > 0 && (
                    <div style={{ marginBottom: 32 }}>
                        <Title level={4} style={{ marginBottom: 16 }}>{t("Services")}</Title>
                        <Table
                            dataSource={servicesByCategory}
                            columns={categoryColumns}
                            rowKey="serviceCategoryId"
                            pagination={false}
                            expandable={{
                                expandedRowRender: renderServiceItems, defaultExpandAllRows: true,
                            }}
                            
                            onRow={(record) => ({
                                style:
                                    record.balance === 0
                                        ? { backgroundColor: '#fff1f0' } // AntD warning yellow
                                        : { backgroundColor: '#f7fbff' },
                            })}
                            summary={() => (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={categoryColumns.length - 1}>
                                        <Typography.Text strong>{t("Total Payable")}</Typography.Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={categoryColumns.length - 1}>
                                        <Typography.Text strong>
                                            <AmountFormat value={totalPayable} currencyCode={claim?.currencyISOCode} />
                                        </Typography.Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                        />
                    </div>
                )}

                {modalType === 'approve' && (
                    <Form.Item name="approvedAmount" label={t('Approved Amount')} rules={[{
                        validator: validateApprovedAmount
                    }
                    ]}>
                        <InputNumber style={{ width: '100%' }} min={0} step={0} placeholder={t('Enter approved amount')}
                            disabled={true}
                            onChange={() => form.validateFields(['reason'])} />
                    </Form.Item>
                )}

                {/* Remarks / Reason */}
                <Form.Item shouldUpdate>
                    {({ getFieldValue }) => {
                        const approved = getFieldValue('approvedAmount') ?? 0;
                        const total = claim?.totalAmount ?? 0;
                        const isReject = modalType === 'reject';
                        const isPartial = modalType === 'approve' && approved < total && approved > 0;
                        const isRequired = isReject || isPartial;
                        return (
                            <Form.Item
                                key={isRequired ? 'required' : 'not-required'}
                                name="reason"
                                label={modalType === 'approve' ? t('Remarks') : t('Reason')}
                                required={isRequired}
                                rules={[{ required: isRequired, message: modalType === 'approve' ? t('Remarks is required for partial approved amount.') : t('Reason is required.') }]}
                            >
                                <Input.TextArea rows={3} placeholder={modalType === 'approve' ? t('Enter remarks (required for partial approval)') : t('Enter reason for rejection')} />
                            </Form.Item>
                        );
                    }}
                </Form.Item>

                <Form.Item style={{ textAlign: 'right' }}>
                    <Button style={{ marginRight: 8 }} onClick={() => router.navigate({ to: '/claim/requests' })}>{t('Cancel')}</Button>
                    <Button type="primary" htmlType="submit" loading={isSubmitting}>{t('Save')}</Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

const ApproveRejectClaimPage = () => {
    const { t } = useTranslation();
    const location = useLocation();

    const validatedState = approveRejectStateSchema.safeParse(location.state);

    if (!validatedState.success) {
        return <div>{t("Invalid page state.")}</div>;
    }

    const { claimId, PolicyHolderId: policyHolderId, type: modalType } = validatedState.data;

    return <ApproveRejectClaimForm claimId={claimId} policyHolderId={policyHolderId} modalType={modalType} />;
};

export default ApproveRejectClaimPage;
